/**
 * ResearchAssistantPage — two-panel AI research chat (light theme).
 * Bug fixed: createChat errors are now surfaced; catch blocks log to console.
 */

import { useState, useEffect, useRef, useCallback } from 'react'
import { Plus, Trash2, Send, Paperclip, Pencil, Check, X, MessageSquare, Loader2, Sparkles, AlertCircle } from 'lucide-react'
import { supabase } from '../../lib/supabase'
import { generateAnthropic } from '../../lib/anthropic'
import { useAIStore } from '../../store/aiStore'

const BG      = '#F4F7FC'
const SURFACE = '#FFFFFF'
const BORDER  = '#DDE4EF'
const TEXT    = '#0C1629'
const MUTED   = '#506280'
const FAINT   = '#8EA4BC'
const GOLD    = '#2563eb'
const GOLD_BG = 'rgba(37,99,235,0.06)'
const PANEL   = '#FAFBFE'

const SUGGESTIONS = [
  'Find gaps in my literature',
  'Suggest theoretical frameworks for my topic',
  'Evaluate my research hypothesis',
  'Suggest survey questions for my methodology',
  'Explain a statistical concept',
  'Review this paragraph for academic quality',
]

function timeAgo(iso) {
  const d = new Date(iso), diff = (Date.now() - d) / 1000
  if (diff < 60)    return 'just now'
  if (diff < 3600)  return `${Math.floor(diff / 60)}m ago`
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
}

function renderInline(text) {
  const parts = [], re = /(\*\*(.+?)\*\*|`(.+?)`)/g
  let last = 0, m
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index))
    if (m[2]) parts.push(<strong key={m.index} style={{ color: TEXT }}>{m[2]}</strong>)
    else if (m[3]) parts.push(
      <code key={m.index} className="font-mono text-xs px-1.5 py-0.5 rounded"
        style={{ background: '#F0F4FA', color: MUTED, border: `1px solid ${BORDER}` }}>
        {m[3]}
      </code>
    )
    last = m.index + m[0].length
  }
  if (last < text.length) parts.push(text.slice(last))
  return parts
}

function renderContent(text) {
  const lines = text.split('\n'), out = []
  let i = 0
  while (i < lines.length) {
    const line = lines[i]
    if (/^\d+\.\s/.test(line)) {
      const items = []
      while (i < lines.length && /^\d+\.\s/.test(lines[i])) { items.push(lines[i].replace(/^\d+\.\s/, '')); i++ }
      out.push(<ol key={`ol-${i}`} className="list-decimal pl-5 space-y-1 my-2">{items.map((it, j) => <li key={j} style={{ color: MUTED }}>{renderInline(it)}</li>)}</ol>)
      continue
    }
    if (/^[-•*]\s/.test(line)) {
      const items = []
      while (i < lines.length && /^[-•*]\s/.test(lines[i])) { items.push(lines[i].replace(/^[-•*]\s/, '')); i++ }
      out.push(<ul key={`ul-${i}`} className="list-disc pl-5 space-y-1 my-2">{items.map((it, j) => <li key={j} style={{ color: MUTED }}>{renderInline(it)}</li>)}</ul>)
      continue
    }
    if (/^#{1,4}\s/.test(line)) {
      const level = line.match(/^(#+)/)[1].length
      const txt = line.replace(/^#+\s/, '')
      const sz = { 1: 'text-base font-bold', 2: 'text-sm font-bold', 3: 'text-sm font-semibold', 4: 'text-sm font-medium' }
      out.push(<p key={i} className={`${sz[level] || 'text-sm font-bold'} mt-3 mb-1`} style={{ color: TEXT }}>{txt}</p>)
    } else if (line.trim()) {
      out.push(<p key={i} className="mb-2 leading-relaxed" style={{ color: MUTED }}>{renderInline(line)}</p>)
    } else {
      out.push(<div key={i} className="h-2" />)
    }
    i++
  }
  return out
}

function Message({ msg }) {
  const isUser = msg.role === 'user'
  if (isUser) {
    return (
      <div className="flex justify-end mb-4">
        <div className="max-w-xl">
          <div className="px-4 py-3 rounded-2xl rounded-tr-sm font-sans text-sm leading-relaxed"
            style={{ background: TEXT, color: '#FFFFFF' }}>
            {msg.content}
          </div>
          {msg.timestamp && (
            <p className="font-mono text-right mt-1" style={{ fontSize: 10, color: FAINT }}>{timeAgo(msg.timestamp)}</p>
          )}
        </div>
      </div>
    )
  }
  return (
    <div className="flex gap-3 mb-5">
      <div className="shrink-0 mt-1 font-sans text-sm font-bold" style={{ color: GOLD }}>✦</div>
      <div className="flex-1 min-w-0">
        <div className="font-sans text-sm leading-relaxed">{renderContent(msg.content)}</div>
        {msg.timestamp && (
          <p className="font-mono mt-1.5" style={{ fontSize: 10, color: FAINT }}>{timeAgo(msg.timestamp)}</p>
        )}
      </div>
    </div>
  )
}

function ChatList({ chats, activeChatId, onSelect, onCreate, onDelete, creating }) {
  return (
    <div className="flex flex-col shrink-0" style={{ width: 252, background: SURFACE, borderRight: `1px solid ${BORDER}` }}>
      <div className="p-3" style={{ borderBottom: `1px solid ${BORDER}` }}>
        <button onClick={onCreate} disabled={creating}
          className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl font-sans text-sm font-medium transition-all disabled:opacity-60"
          style={{ border: `1px solid rgba(37,99,235,0.35)`, color: GOLD, background: 'transparent' }}
          onMouseEnter={e => { if (!creating) e.currentTarget.style.background = GOLD_BG }}
          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
          {creating
            ? <Loader2 style={{ width: 13, height: 13, animation: 'spin 1s linear infinite' }} />
            : <Plus style={{ width: 13, height: 13 }} />}
          New Research Chat
        </button>
      </div>

      <div className="flex-1 overflow-y-auto py-2 scrollbar-thin">
        {chats.length === 0 ? (
          <div className="px-4 py-8 text-center">
            <MessageSquare style={{ width: 22, height: 22, color: FAINT, margin: '0 auto 8px' }} />
            <p className="font-sans text-xs" style={{ color: FAINT }}>Start your first research conversation</p>
          </div>
        ) : chats.map(chat => {
          const active = chat.id === activeChatId
          return (
            <div key={chat.id} onClick={() => onSelect(chat)}
              className="group flex items-start gap-2 mx-2 px-3 py-2.5 rounded-lg cursor-pointer transition-all"
              style={{ background: active ? GOLD_BG : 'transparent', borderLeft: active ? `2px solid ${GOLD}` : '2px solid transparent' }}
              onMouseEnter={e => { if (!active) e.currentTarget.style.background = '#F4F7FC' }}
              onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent' }}>
              <div className="flex-1 min-w-0">
                <p className="font-sans text-xs font-medium truncate" style={{ color: active ? GOLD : TEXT }}>
                  {chat.title || 'New Research Chat'}
                </p>
                <p className="font-mono mt-0.5" style={{ fontSize: 10, color: FAINT }}>{timeAgo(chat.updated_at || chat.created_at)}</p>
              </div>
              <button onClick={e => { e.stopPropagation(); onDelete(chat.id) }}
                className="shrink-0 opacity-0 group-hover:opacity-100 p-1 rounded transition-all"
                style={{ color: FAINT }}
                onMouseEnter={e => { e.currentTarget.style.color = '#DC2626'; e.currentTarget.style.background = '#FEF2F2' }}
                onMouseLeave={e => { e.currentTarget.style.color = FAINT; e.currentTarget.style.background = 'transparent' }}>
                <Trash2 style={{ width: 12, height: 12 }} />
              </button>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function ChatTopBar({ chat, onRename, onDelete }) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft]     = useState(chat?.title || '')
  const inputRef              = useRef(null)

  useEffect(() => setDraft(chat?.title || ''), [chat?.title])
  useEffect(() => { if (editing) inputRef.current?.focus() }, [editing])

  const commit = () => {
    if (draft.trim() && draft !== chat?.title) onRename(draft.trim())
    setEditing(false)
  }

  return (
    <div className="flex items-center gap-3 px-5 py-3 shrink-0"
      style={{ background: SURFACE, borderBottom: `1px solid ${BORDER}`, height: 52 }}>
      {editing ? (
        <>
          <input ref={inputRef} value={draft} onChange={e => setDraft(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') setEditing(false) }}
            className="flex-1 px-2 py-1 rounded-lg font-sans text-sm outline-none"
            style={{ border: `1px solid rgba(37,99,235,0.4)`, color: TEXT, background: '#F4F7FC',
                     boxShadow: '0 0 0 3px rgba(37,99,235,0.10)' }} />
          <button onClick={commit}
            className="p-1.5 rounded transition-all"
            onMouseEnter={e => e.currentTarget.style.background = '#F0FDF4'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
            <Check style={{ width: 14, height: 14, color: '#16A34A' }} />
          </button>
          <button onClick={() => setEditing(false)}
            className="p-1.5 rounded transition-all"
            onMouseEnter={e => e.currentTarget.style.background = '#F4F7FC'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
            <X style={{ width: 14, height: 14, color: FAINT }} />
          </button>
        </>
      ) : (
        <>
          <p className="flex-1 font-sans text-sm font-semibold truncate cursor-pointer transition-colors"
            style={{ color: TEXT }}
            onClick={() => setEditing(true)}
            title="Click to rename"
            onMouseEnter={e => e.currentTarget.style.color = GOLD}
            onMouseLeave={e => e.currentTarget.style.color = TEXT}>
            {chat?.title || 'New Research Chat'}
          </p>
          <button onClick={() => setEditing(true)} title="Rename"
            className="p-1.5 rounded-lg transition-all"
            style={{ color: FAINT }}
            onMouseEnter={e => { e.currentTarget.style.background = '#F4F7FC'; e.currentTarget.style.color = MUTED }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = FAINT }}>
            <Pencil style={{ width: 13, height: 13 }} />
          </button>
          <button onClick={onDelete} title="Delete chat"
            className="p-1.5 rounded-lg transition-all"
            style={{ color: FAINT }}
            onMouseEnter={e => { e.currentTarget.style.background = '#FEF2F2'; e.currentTarget.style.color = '#DC2626' }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = FAINT }}>
            <Trash2 style={{ width: 13, height: 13 }} />
          </button>
        </>
      )}
    </div>
  )
}

function EmptyChat({ onSuggest }) {
  return (
    <div className="flex-1 flex flex-col items-center justify-center px-8 py-12">
      <div className="w-11 h-11 rounded-2xl flex items-center justify-center mb-4"
        style={{ background: GOLD_BG, border: `1px solid rgba(37,99,235,0.25)` }}>
        <Sparkles style={{ width: 20, height: 20, color: GOLD }} />
      </div>
      <p className="font-sans text-base font-semibold mb-1" style={{ color: TEXT }}>How can I help with your research?</p>
      <p className="font-sans text-sm mb-8 text-center" style={{ color: FAINT }}>
        Ask anything — methodology, literature, frameworks, statistics.
      </p>
      <div className="grid grid-cols-2 gap-2 w-full max-w-lg">
        {SUGGESTIONS.map(s => (
          <button key={s} onClick={() => onSuggest(s)}
            className="text-left px-3.5 py-2.5 rounded-xl font-sans text-xs transition-all"
            style={{ border: `1px solid rgba(37,99,235,0.25)`, color: GOLD, background: GOLD_BG }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(37,99,235,0.10)'}
            onMouseLeave={e => e.currentTarget.style.background = GOLD_BG}>
            {s}
          </button>
        ))}
      </div>
    </div>
  )
}

// ─── Main ─────────────────────────────────────────────────────────────────────

export default function ResearchAssistantPage() {
  const aiStore = useAIStore()

  const [chats,      setChats]      = useState([])
  const [activeChat, setActiveChat] = useState(null)
  const [messages,   setMessages]   = useState([])
  const [input,      setInput]      = useState('')
  const [streaming,  setStreaming]  = useState(false)
  const [creating,   setCreating]   = useState(false)
  const [loadError,  setLoadError]  = useState('')

  const bottomRef   = useRef(null)
  const textareaRef = useRef(null)

  useEffect(() => { loadChats() }, [])
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }) }, [messages])

  const loadChats = async () => {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) { setLoadError('Not authenticated'); return }
    const { data, error } = await supabase
      .from('research_chats')
      .select('*')
      .eq('user_id', user.id)
      .order('updated_at', { ascending: false })
    if (error) setLoadError('Could not load chats. Run migration 001_research_references.sql in Supabase.')
    else setChats(data || [])
  }

  const selectChat = chat => {
    setActiveChat(chat)
    setMessages(chat.messages || [])
  }

  const createChat = async () => {
    if (creating) return
    setCreating(true)
    setLoadError('')
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) { setLoadError('Not authenticated'); return }
      const { data, error } = await supabase
        .from('research_chats')
        .insert({ user_id: user.id, title: 'New Research Chat', messages: [] })
        .select()
        .single()
      if (error) throw error
      setChats(prev => [data, ...prev])
      selectChat(data)
    } catch (err) {
      setLoadError('Failed to create chat: ' + (err.message || 'unknown error'))
    } finally {
      setCreating(false)
    }
  }

  const deleteChat = async id => {
    await supabase.from('research_chats').delete().eq('id', id)
    const remaining = chats.filter(c => c.id !== id)
    setChats(remaining)
    if (activeChat?.id === id) {
      setActiveChat(remaining[0] || null)
      setMessages(remaining[0]?.messages || [])
    }
  }

  const renameChat = async title => {
    if (!activeChat) return
    const { data, error } = await supabase
      .from('research_chats')
      .update({ title, updated_at: new Date().toISOString() })
      .eq('id', activeChat.id)
      .select()
      .single()
    if (!error && data) {
      setChats(prev => prev.map(c => c.id === data.id ? data : c))
      setActiveChat(data)
    }
  }

  const send = useCallback(async text => {
    const msg = (text || input).trim()
    if (!msg || !activeChat || streaming) return
    setInput('')
    if (textareaRef.current) textareaRef.current.style.height = 'auto'

    // Capture history before state updates (stale closure = prior messages)
    const priorMessages = messages

    const userMsg = { role: 'user', content: msg, timestamp: new Date().toISOString() }
    setMessages(prev => [...prev, userMsg])
    setStreaming(true)
    const aiPlaceholder = { role: 'assistant', content: '', timestamp: new Date().toISOString() }
    setMessages(prev => [...prev, aiPlaceholder])

    try {
      aiStore.assertEnabled()

      const history = priorMessages
        .filter(m => (m.role === 'user' || m.role === 'assistant') && m.content?.trim())
        .map(m => ({ role: m.role, content: m.content }))
      history.push({ role: 'user', content: msg })

      const aiContent = await generateAnthropic({
        feature:   'research_chat',
        maxTokens: 2048,
        system:    'You are an expert academic research assistant helping PhD students with their research. Provide clear, scholarly, and actionable guidance. Reference relevant theories, methodologies, and literature where appropriate.',
        messages:  history,
      })

      aiStore.increment()

      const aiMsg = { role: 'assistant', content: aiContent, timestamp: new Date().toISOString() }
      setMessages(prev => {
        const copy = [...prev]
        copy[copy.length - 1] = { ...copy[copy.length - 1], content: aiContent }
        return copy
      })

      const allMessages = [...priorMessages, userMsg, aiMsg]
      await supabase
        .from('research_chats')
        .update({ messages: allMessages, updated_at: new Date().toISOString() })
        .eq('id', activeChat.id)

    } catch (err) {
      setMessages(prev => {
        const copy = [...prev]
        copy[copy.length - 1] = { ...copy[copy.length - 1], content: `⚠ ${err.message || 'Failed to get response. Please try again.'}` }
        return copy
      })
    } finally {
      setStreaming(false)
    }
  }, [activeChat, input, streaming, messages])

  const handleKeyDown = e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send() } }
  const handleInput   = e => {
    setInput(e.target.value)
    const ta = e.target
    ta.style.height = 'auto'
    ta.style.height = Math.min(ta.scrollHeight, 144) + 'px'
  }

  return (
    <div className="flex" style={{ height: 'calc(100vh - 3.5rem)', margin: '-24px', overflow: 'hidden' }}>
      <ChatList
        chats={chats}
        activeChatId={activeChat?.id}
        onSelect={selectChat}
        onCreate={createChat}
        onDelete={deleteChat}
        creating={creating}
      />

      {/* Right panel */}
      <div className="flex-1 flex flex-col min-w-0" style={{ background: PANEL }}>

        {/* Error banner */}
        {loadError && (
          <div className="flex items-start gap-3 mx-4 mt-3 px-4 py-3 rounded-xl text-sm"
            style={{ background: '#FEF2F2', border: '1px solid rgba(220,38,38,0.2)', color: '#DC2626' }}>
            <AlertCircle style={{ width: 15, height: 15, shrink: 0, marginTop: 1 }} />
            <div>
              <span className="font-medium">Error: </span>{loadError}
            </div>
            <button onClick={() => setLoadError('')} className="ml-auto shrink-0"><X style={{ width: 14, height: 14 }} /></button>
          </div>
        )}

        {!activeChat ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-4">
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center"
              style={{ background: GOLD_BG, border: `1px solid rgba(37,99,235,0.25)` }}>
              <Sparkles style={{ width: 22, height: 22, color: GOLD }} />
            </div>
            <div className="text-center">
              <p className="font-sans text-sm font-semibold mb-1" style={{ color: TEXT }}>Research Assistant</p>
              <p className="font-sans text-xs" style={{ color: FAINT }}>Create a new chat to get started</p>
            </div>
            <button onClick={createChat} disabled={creating}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl font-sans text-sm font-medium transition-all disabled:opacity-60"
              style={{ border: `1px solid rgba(37,99,235,0.35)`, color: GOLD, background: GOLD_BG }}>
              {creating
                ? <Loader2 style={{ width: 14, height: 14, animation: 'spin 1s linear infinite' }} />
                : <Plus style={{ width: 14, height: 14 }} />}
              New Research Chat
            </button>
          </div>
        ) : (
          <>
            <ChatTopBar chat={activeChat} onRename={renameChat} onDelete={() => deleteChat(activeChat.id)} />

            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-6 py-5 scrollbar-thin">
              {messages.length === 0
                ? <EmptyChat onSuggest={s => { setInput(s); textareaRef.current?.focus() }} />
                : (
                  <>
                    {messages.map((msg, i) => <Message key={i} msg={msg} />)}
                    {streaming && messages[messages.length - 1]?.content === '' && (
                      <div className="flex gap-3 mb-4">
                        <div className="font-sans text-sm font-bold" style={{ color: GOLD }}>✦</div>
                        <div className="flex items-center gap-1.5 pt-1">
                          {[0, 1, 2].map(i => (
                            <span key={i} className="w-1.5 h-1.5 rounded-full"
                              style={{ background: GOLD, animation: `bounce 1.2s ${i * 0.2}s ease-in-out infinite` }} />
                          ))}
                        </div>
                      </div>
                    )}
                    <div ref={bottomRef} />
                  </>
                )
              }
            </div>

            {/* Input area */}
            <div className="px-4 pb-4 pt-2 shrink-0" style={{ borderTop: `1px solid ${BORDER}`, background: SURFACE }}>
              <div className="relative flex items-end gap-2.5 max-w-3xl mx-auto">
                <div className="flex-1 flex items-end rounded-2xl overflow-hidden transition-all"
                  style={{ background: '#F4F7FC', border: `1px solid ${BORDER}` }}
                  onFocusCapture={e => e.currentTarget.style.borderColor = 'rgba(37,99,235,0.4)'}
                  onBlurCapture={e => e.currentTarget.style.borderColor = BORDER}>
                  <textarea ref={textareaRef} value={input} onChange={handleInput} onKeyDown={handleKeyDown}
                    placeholder="Ask anything about your research…" rows={1}
                    style={{
                      flex: 1, resize: 'none', background: 'transparent', border: 'none', outline: 'none',
                      padding: '12px 14px', fontFamily: 'Sora, sans-serif', fontSize: 14,
                      color: TEXT, lineHeight: 1.5, maxHeight: 144, minHeight: 44, overflowY: 'auto',
                    }} />
                  <button className="p-2 mr-2 mb-2 shrink-0 rounded-lg transition-colors"
                    style={{ color: FAINT }}
                    onMouseEnter={e => { e.currentTarget.style.background = '#FFFFFF'; e.currentTarget.style.color = MUTED }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = FAINT }}
                    title="Attach text">
                    <Paperclip style={{ width: 15, height: 15 }} />
                  </button>
                </div>
                <button onClick={() => send()} disabled={!input.trim() || streaming}
                  className="flex items-center justify-center w-10 h-10 rounded-full shrink-0 transition-all disabled:opacity-40"
                  style={{ background: GOLD }}>
                  {streaming
                    ? <Loader2 style={{ width: 15, height: 15, color: '#FFFFFF', animation: 'spin 1s linear infinite' }} />
                    : <Send style={{ width: 15, height: 15, color: '#FFFFFF' }} />}
                </button>
              </div>
              <p className="text-center font-mono mt-1.5" style={{ fontSize: 10, color: FAINT }}>
                Enter to send · Shift+Enter for new line
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
